// PostgreSQL parseable-body fetcher (Slice 17 dispatch arm).
//
// Per the slice, PostgreSQL exposes:
//   - views               → `pg_views` (excludes system schemas)
//   - inline TVFs         → `pg_proc` rows whose result type starts with
//                            `TABLE` (or `SETOF`) and whose language is `sql`
//   - scalar SQL functions → `pg_proc` rows whose result is non-TABLE and
//                            whose language is `sql`
//   - default constraints → `pg_attrdef.adbin`/`pg_get_expr`
//   - check constraints   → `pg_constraint WHERE contype='c'`
//
// PL/pgSQL function bodies are inherited via the procedural grammar in a
// future slice; this fetcher restricts to `lanname='sql'` so only bodies the
// resolver-side parseDocument can handle land in the index.

import type {
  ObjectKind,
  PersistedObjectListing,
  QualifiedName,
} from '../../types';

import {
  asString,
  escapeSqlLiteral,
  fetchListingRows,
  fetchRows,
  makeListing,
  type NavListingFetchOptions,
} from './queryHelper';

function listing(
  objectKind: PersistedObjectListing['objectKind'],
  schema: string,
  name: string,
  bodyText: string,
): PersistedObjectListing {
  return makeListing('postgres', objectKind, schema, name, bodyText);
}

const SYSTEM_SCHEMAS = `('pg_catalog', 'information_schema', 'pg_toast')`;

const VIEWS_SQL = `SELECT schemaname AS schema, viewname AS name, definition AS body
FROM pg_catalog.pg_views
WHERE schemaname NOT IN ${SYSTEM_SCHEMAS}`;

// Functions written in plain SQL: pg_get_functiondef returns the full
// `CREATE FUNCTION ... AS $$ ... $$ ...` text. Filter on `lanname='sql'` so
// PL/pgSQL bodies stay out of this fetcher (they need the procedural grammar).
const FUNCTIONS_SQL = `SELECT n.nspname AS schema, p.proname AS name,
  pg_get_functiondef(p.oid) AS body,
  pg_catalog.pg_get_function_result(p.oid) AS result_type
FROM pg_catalog.pg_proc p
JOIN pg_catalog.pg_namespace n ON n.oid = p.pronamespace
JOIN pg_catalog.pg_language l ON l.oid = p.prolang
WHERE l.lanname = 'sql'
  AND n.nspname NOT IN ${SYSTEM_SCHEMAS}
  AND p.prokind = 'f'`;

const DEFAULTS_SQL = `SELECT n.nspname AS schema,
  c.relname || '.' || a.attname AS name,
  pg_get_expr(d.adbin, d.adrelid) AS body
FROM pg_catalog.pg_attrdef d
JOIN pg_catalog.pg_attribute a ON a.attrelid = d.adrelid AND a.attnum = d.adnum
JOIN pg_catalog.pg_class c ON c.oid = d.adrelid
JOIN pg_catalog.pg_namespace n ON n.oid = c.relnamespace
WHERE n.nspname NOT IN ${SYSTEM_SCHEMAS}`;

const CHECKS_SQL = `SELECT n.nspname AS schema, c.conname AS name,
  pg_get_constraintdef(c.oid) AS body
FROM pg_catalog.pg_constraint c
JOIN pg_catalog.pg_namespace n ON n.oid = c.connamespace
WHERE c.contype = 'c'
  AND n.nspname NOT IN ${SYSTEM_SCHEMAS}`;

function isInlineTvfResult(resultType: string): boolean {
  // `pg_get_function_result` returns text like 'TABLE(id integer, …)' for
  // table-valued functions and 'SETOF record' for set-returning scalar funcs.
  // Both are reasonable candidates for the inline-TVF bucket; `parseDocument`
  // handles the body identically since the grammar covers `RETURNS TABLE` /
  // `RETURNS SETOF` shapes the same way.
  const trimmed = resultType.trim().toUpperCase();
  return trimmed.startsWith('TABLE') || trimmed.startsWith('SETOF');
}

// Page keys. `pg_proc` / `pg_attrdef` / `pg_constraint` are real catalog
// tables, so their `oid` is a unique total order. DIVERGENCE: `pg_views` is a
// VIEW over `pg_class` and exposes no oid, so its stable key is the qualified
// name — unique because a relation name cannot repeat within a schema.
export async function listPgsqlParseableObjects(
  options: NavListingFetchOptions,
): Promise<readonly PersistedObjectListing[]> {
  const [viewRows, functionRows, defaultRows, checkRows] = await Promise.all([
    fetchListingRows(options, {
      engine: 'pgsql',
      label: 'pg-views',
      sql: VIEWS_SQL,
      orderBy: 'schemaname, viewname',
      dialect: 'limit-offset',
      bodyColumns: ['body'],
    }),
    fetchListingRows(options, {
      engine: 'pgsql',
      label: 'pg-functions',
      sql: FUNCTIONS_SQL,
      orderBy: 'p.oid',
      dialect: 'limit-offset',
      bodyColumns: ['body'],
    }),
    fetchListingRows(options, {
      engine: 'pgsql',
      label: 'pg-defaults',
      sql: DEFAULTS_SQL,
      orderBy: 'd.oid',
      dialect: 'limit-offset',
      bodyColumns: ['body'],
    }),
    fetchListingRows(options, {
      engine: 'pgsql',
      label: 'pg-checks',
      sql: CHECKS_SQL,
      orderBy: 'c.oid',
      dialect: 'limit-offset',
      bodyColumns: ['body'],
    }),
  ]);

  const out: PersistedObjectListing[] = [];

  for (const row of viewRows) {
    const body = asString(row.body);
    const name = asString(row.name);
    if (!name || !body) continue;
    out.push(listing('view', asString(row.schema), name, body));
  }

  for (const row of functionRows) {
    const body = asString(row.body);
    const name = asString(row.name);
    if (!name || !body) continue;
    const kind: PersistedObjectListing['objectKind'] = isInlineTvfResult(asString(row.result_type))
      ? 'inline-tvf'
      : 'scalar-function';
    out.push(listing(kind, asString(row.schema), name, body));
  }

  for (const row of defaultRows) {
    const body = asString(row.body);
    const name = asString(row.name);
    if (!name || !body) continue;
    out.push(listing('default-constraint', asString(row.schema), name, body));
  }

  for (const row of checkRows) {
    const body = asString(row.body);
    const name = asString(row.name);
    if (!name || !body) continue;
    out.push(listing('check-constraint', asString(row.schema), name, body));
  }

  return out;
}

/** Schemas holding an object named `name` (already in catalog spelling) of
 *  any of `kinds` — the DDL invalidator's "where did this bare statement
 *  run?" probe. One UNION per catalog the kinds live in. */
export async function listPgsqlSchemasHoldingObject(
  connectionId: string,
  database: string,
  name: string,
  kinds: readonly ObjectKind[],
): Promise<readonly string[]> {
  const n = escapeSqlLiteral(name);
  const parts: string[] = [];
  if (kinds.includes('view')) {
    parts.push(`SELECT n.nspname AS schema FROM pg_catalog.pg_class c
      JOIN pg_catalog.pg_namespace n ON n.oid = c.relnamespace
      WHERE c.relname = '${n}' AND c.relkind = 'v'`);
  }
  const prokinds = [
    ...(kinds.includes('inline-tvf') || kinds.includes('scalar-function') ? ["'f'"] : []),
    ...(kinds.includes('procedure') ? ["'p'"] : []),
  ];
  if (prokinds.length > 0) {
    parts.push(`SELECT n.nspname AS schema FROM pg_catalog.pg_proc p
      JOIN pg_catalog.pg_namespace n ON n.oid = p.pronamespace
      WHERE p.proname = '${n}' AND p.prokind IN (${prokinds.join(', ')})`);
  }
  if (kinds.includes('trigger')) {
    parts.push(`SELECT n.nspname AS schema FROM pg_catalog.pg_trigger t
      JOIN pg_catalog.pg_class c ON c.oid = t.tgrelid
      JOIN pg_catalog.pg_namespace n ON n.oid = c.relnamespace
      WHERE t.tgname = '${n}' AND NOT t.tgisinternal`);
  }
  if (parts.length === 0) return [];
  const sql = `SELECT DISTINCT schema FROM (${parts.join('\n      UNION ')}) AS held`;
  const rows = await fetchRows(connectionId, database, sql, 'pg-schemas-of');
  return rows.map((r) => asString(r.schema)).filter((s) => s.length > 0);
}

export async function fetchPgsqlObjectBody(
  connectionId: string,
  database: string,
  objectKind: ObjectKind,
  objectName: QualifiedName,
): Promise<string | null> {
  const schema = objectName.schema ?? 'public';
  const s = escapeSqlLiteral(schema);
  const n = escapeSqlLiteral(objectName.name);

  if (objectKind === 'view') {
    const sql = `SELECT definition AS body FROM pg_catalog.pg_views
      WHERE schemaname = '${s}' AND viewname = '${n}'`;
    const rows = await fetchRows(connectionId, database, sql, 'pg-view');
    return asString(rows[0]?.body) || null;
  }

  if (objectKind === 'inline-tvf' || objectKind === 'scalar-function') {
    // Same filters and the same kind rule as `FUNCTIONS_SQL` above: the DDL
    // invalidator asks for the name under BOTH kinds, and only the kind the
    // listing would file it under may answer — otherwise one function is
    // indexed twice, once as a kind it is not.
    const sql = `SELECT pg_get_functiondef(p.oid) AS body,
        pg_catalog.pg_get_function_result(p.oid) AS result_type
      FROM pg_catalog.pg_proc p
      JOIN pg_catalog.pg_namespace n ON n.oid = p.pronamespace
      JOIN pg_catalog.pg_language l ON l.oid = p.prolang
      WHERE n.nspname = '${s}' AND p.proname = '${n}'
        AND l.lanname = 'sql' AND p.prokind = 'f'`;
    const rows = await fetchRows(connectionId, database, sql, 'pg-function');
    // Overloads come back as several rows; pick the one of the REQUESTED kind
    // so a scalar `f(integer)` next to a set-returning `f(text)` keeps both
    // kinds' records. (The index has no signature dimension, so several
    // overloads of the SAME kind collapse to the first one's body here — as
    // they always did.)
    const row = rows.find(
      (r) => (isInlineTvfResult(asString(r.result_type)) ? 'inline-tvf' : 'scalar-function') === objectKind,
    );
    if (!row) return null;
    return asString(row.body) || null;
  }

  if (objectKind === 'check-constraint') {
    const sql = `SELECT pg_get_constraintdef(c.oid) AS body
      FROM pg_catalog.pg_constraint c
      JOIN pg_catalog.pg_namespace n ON n.oid = c.connamespace
      WHERE n.nspname = '${s}' AND c.conname = '${n}' AND c.contype = 'c'`;
    const rows = await fetchRows(connectionId, database, sql, 'pg-check');
    return asString(rows[0]?.body) || null;
  }

  if (objectKind === 'default-constraint') {
    // Default-constraint name is `<table>.<column>` per the listing; split.
    const dot = objectName.name.lastIndexOf('.');
    if (dot < 0) return null;
    const table = escapeSqlLiteral(objectName.name.slice(0, dot));
    const column = escapeSqlLiteral(objectName.name.slice(dot + 1));
    const sql = `SELECT pg_get_expr(d.adbin, d.adrelid) AS body
      FROM pg_catalog.pg_attrdef d
      JOIN pg_catalog.pg_attribute a ON a.attrelid = d.adrelid AND a.attnum = d.adnum
      JOIN pg_catalog.pg_class c ON c.oid = d.adrelid
      JOIN pg_catalog.pg_namespace n ON n.oid = c.relnamespace
      WHERE n.nspname = '${s}' AND c.relname = '${table}' AND a.attname = '${column}'`;
    const rows = await fetchRows(connectionId, database, sql, 'pg-default');
    return asString(rows[0]?.body) || null;
  }

  return null;
}

// Procedural feed (ProceduralBodyIndexing Slice 2.1). The implementation lives
// in `pgsqlProceduralFetcher.ts` so this file stays under the 200-line budget;
// we re-export here to keep the dispatcher's import path stable.
export {
  listPgsqlProceduralObjects,
  fetchPgsqlProceduralObjectBody,
  stripPgProceduralWrapper,
} from './pgsqlProceduralFetcher';
